import type { EnterpriseNotification } from "@swot/swot-components";
import type { CmtsHttpClient } from "../api/cmtsHttpClient.js";
import { CMTS_API } from "../api/cmtsApiPaths.js";
import { mapNotification } from "../mappers/notificationMappers.js";
import { mapCollection } from "./repositoryCollection.js";
import { repositoryCall, type RepositoryResult } from "./repositoryResult.js";
import { requireCmtsGuid } from "../utils/cmtsGuid.js";

export type NotificationRepository = {
  my(tenantId?: string): Promise<RepositoryResult<readonly EnterpriseNotification[]>>;
  markRead(id: string, tenantId?: string): Promise<RepositoryResult<EnterpriseNotification>>;
  markUnread(id: string, tenantId?: string): Promise<RepositoryResult<EnterpriseNotification>>;
  acknowledge(id: string, payload: Record<string, unknown>, tenantId?: string): Promise<RepositoryResult<EnterpriseNotification>>;
  archive(id: string, payload: Record<string, unknown>, tenantId?: string): Promise<RepositoryResult<EnterpriseNotification>>;
  executeAction(id: string, payload: Record<string, unknown>, tenantId?: string): Promise<RepositoryResult<EnterpriseNotification>>;
  createTask(id: string, payload: Record<string, unknown>, tenantId?: string): Promise<RepositoryResult<unknown>>;
  dispatchPending(tenantId?: string): Promise<RepositoryResult<unknown>>;
  channelHealth(tenantId?: string): Promise<RepositoryResult<unknown>>;
  saveTemplate(payload: Record<string, unknown>): Promise<RepositoryResult<unknown>>;
  retryDispatchAttempt(attemptId: string, tenantId?: string): Promise<RepositoryResult<unknown>>;
};


export function createNotificationRepository(client: CmtsHttpClient): NotificationRepository {
  const map = async (p: Promise<unknown>) => mapNotification(await p);
  const query = (tenantId?: string) => ({ query: { tenantId } });
  return {
    my: (tenantId) => repositoryCall(async () => mapCollection(await client.get<unknown>(CMTS_API.notifications.my, query(tenantId)), mapNotification), "CMTS_NOTIFICATIONS_MY"),
    markRead: (id, tenantId) => repositoryCall(() => {
      const notificationId = requireCmtsGuid(id, "Notification mark read id");
      return map(client.post<unknown>(CMTS_API.notifications.markRead(notificationId), {}, query(tenantId)));
    }, "CMTS_NOTIFICATION_READ"),
    markUnread: (id, tenantId) => repositoryCall(async () => {
      const notificationId = requireCmtsGuid(id, "Notification mark unread id");
      await client.post<unknown>(CMTS_API.notifications.markUnread(notificationId), {}, query(tenantId));
      return mapNotification({ id: notificationId, status: "UNREAD", isRead: false });
    }, "CMTS_NOTIFICATION_UNREAD"),
    acknowledge: (id, payload, tenantId) => repositoryCall(async () => {
      const notificationId = requireCmtsGuid(id, "Notification acknowledge id");
      await client.post<unknown>(CMTS_API.notifications.acknowledge(notificationId), payload, query(tenantId));
      return mapNotification({ id: notificationId, status: "ACKNOWLEDGED", isRead: true });
    }, "CMTS_NOTIFICATION_ACKNOWLEDGE"),
    archive: (id, payload, tenantId) => repositoryCall(async () => {
      const notificationId = requireCmtsGuid(id, "Notification archive id");
      await client.post<unknown>(CMTS_API.notifications.archive(notificationId), payload, query(tenantId));
      return mapNotification({ id: notificationId, status: "ARCHIVED", isRead: true });
    }, "CMTS_NOTIFICATION_ARCHIVE"),
    executeAction: (id, payload, tenantId) => repositoryCall(async () => {
      const notificationId = requireCmtsGuid(id, "Notification action id");
      await client.post<unknown>(CMTS_API.notifications.action(notificationId), payload, query(tenantId));
      return mapNotification({ id: notificationId, status: "ACKNOWLEDGED", isRead: true });
    }, "CMTS_NOTIFICATION_ACTION"),
    createTask: (id, payload, tenantId) => repositoryCall(() => {
      const notificationId = requireCmtsGuid(id, "Notification create task id");
      return client.post<unknown>(CMTS_API.notifications.createTask(notificationId), payload, query(tenantId));
    }, "CMTS_NOTIFICATION_CREATE_TASK"),
    dispatchPending: (tenantId) => repositoryCall(() => client.post<unknown>(CMTS_API.notifications.dispatchPending, {}, query(tenantId)), "CMTS_NOTIFICATION_DISPATCH_PENDING"),
    channelHealth: (tenantId) => repositoryCall(() => client.get<unknown>(CMTS_API.notifications.channelHealth, query(tenantId)), "CMTS_NOTIFICATION_CHANNEL_HEALTH"),
    saveTemplate: (payload) => repositoryCall(() => client.post<unknown>(CMTS_API.notifications.templates, payload), "CMTS_NOTIFICATION_TEMPLATE_SAVE"),
    retryDispatchAttempt: (attemptId, tenantId) => repositoryCall(() => client.post<unknown>(CMTS_API.notificationProviders.retryAttempt(attemptId), {}, query(tenantId)), "CMTS_NOTIFICATION_ATTEMPT_RETRY"),
  };
}
